import { useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUp, Loader2, Sparkles, ReceiptText } from "lucide-react";
import { VendorMatchHint } from "@/components/VendorMatchHint";
import { CategoryField } from "@/components/entity/CategoryField";

export function InvoiceUploader({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [fields, setFields] = useState(null);
  const [preview, setPreview] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setFields(null);
    setPreviewing(false);
    setParsing(false);
    setPosting(false);
  };

  const previewFile = async () => {
    if (!file) return toast.error("Choose an invoice PDF first");
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not read invoice");
    } finally {
      setPreviewing(false);
    }
  };

  const parse = async () => {
    if (!file) return toast.error("Choose an invoice PDF first");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/parse-async", fd);
      let job = data;
      const started = Date.now();
      while (job.status === "processing" && Date.now() - started < 300000) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await api.get(`/invoices/parse-jobs/${data.job_id}`);
        job = { ...res.data, job_id: data.job_id };
      }
      if (job.status === "error") throw new Error(job.error || "Could not parse invoice");
      if (job.status !== "done") throw new Error("Parsing timed out — please try again");
      setFields(job.invoice || {});
      toast.success("Invoice fields extracted — review before saving");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not parse invoice");
    } finally {
      setParsing(false);
    }
  };

  const setField = (k, v) =>
    setFields((f) => {
      if (k === "vendor" && v !== f.vendor) {
        // Vendor name was edited by hand — the extraction-time match no longer
        // applies, so clear the hints until the user links/creates a vendor.
        return { ...f, vendor: v, matched_vendor: undefined, vendor_suggestion: undefined, similar_vendors: undefined };
      }
      return { ...f, [k]: v };
    });

  const post = async () => {
    if (!fields?.vendor || !fields?.amount) {
      return toast.error("Vendor name and amount are required");
    }
    setPosting(true);
    try {
      const base = Number(fields.sub_total) > 0 ? Number(fields.sub_total) : Number(fields.amount || 0);
      const tds_amount = Math.round(base * (Number(fields.tds_rate) || 0)) / 100;
      const { data } = await api.post("/invoices/post-as-payable", { ...fields, tds_amount });
      toast.success(`Payable created for ${data.vendor}`);
      setOpen(false);
      reset();
      onPosted && onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save payable");
    } finally {
      setPosting(false);
    }
  };

  let stage = "pick";
  if (fields) stage = "review";
  else if (preview) stage = "preview";

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="open-invoice-upload-btn" className="gap-2">
        <FileUp className="h-4 w-4" /> Upload Invoice
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Vendor Invoice</DialogTitle>
            <DialogDescription>
              {stage === "pick" && "Upload a PDF invoice from your vendor."}
              {stage === "preview" && "Confirm the file preview looks right before AI extraction."}
              {stage === "review" && "Review and edit the extracted fields, then save as a Payable."}
            </DialogDescription>
          </DialogHeader>

          {stage === "pick" && (
            <div className="py-2">
              <Label className="text-xs mb-1.5 block">Invoice PDF</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="invoice-file-input"
              />
              <p className="text-xs text-muted-foreground mt-2">Max 25MB. AI understands most tax invoice layouts.</p>
            </div>
          )}

          {stage === "preview" && (
            <div className="py-2 space-y-3" data-testid="invoice-preview-panel">
              <p className="text-sm">
                <span className="font-medium">{file?.name}</span> · {preview.pages} page{preview.pages === 1 ? "" : "s"} · {preview.text_chars} chars of text.
              </p>
              <p className="text-xs text-muted-foreground">
                Confirm to run AI extraction — you&apos;ll review every field before it&apos;s saved.
              </p>
            </div>
          )}

          {stage === "review" && (
            <div className="py-1 grid grid-cols-2 gap-3" data-testid="invoice-review-panel">
              <div className="col-span-2">
                <Label className="text-xs">Vendor</Label>
                <Input value={fields.vendor || ""} onChange={(e) => setField("vendor", e.target.value)} data-testid="inv-vendor" className="mt-1" />
                <VendorMatchHint fields={fields} setFields={setFields} />
              </div>
              <div>
                <Label className="text-xs">Invoice No.</Label>
                <Input value={fields.invoice_no || ""} onChange={(e) => setField("invoice_no", e.target.value)} data-testid="inv-number" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <div className="mt-1">
                  <CategoryField catType="expense" value={fields.category || ""} onChange={(v) => setField("category", v)} testid="inv-category" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Invoice Date</Label>
                <Input type="date" value={fields.invoice_date || ""} onChange={(e) => setField("invoice_date", e.target.value)} data-testid="inv-date" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={fields.due_date || ""} onChange={(e) => setField("due_date", e.target.value)} data-testid="inv-due-date" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Sub-total</Label>
                <Input type="number" step="0.01" value={fields.sub_total || 0} onChange={(e) => setField("sub_total", Number(e.target.value))} data-testid="inv-subtotal" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">GST / Tax</Label>
                <Input type="number" step="0.01" value={fields.gst_amount || 0} onChange={(e) => setField("gst_amount", Number(e.target.value))} data-testid="inv-gst" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Total Payable</Label>
                <Input type="number" step="0.01" value={fields.amount || 0} onChange={(e) => setField("amount", Number(e.target.value))} data-testid="inv-amount" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <select value={fields.currency || "INR"} onChange={(e) => setField("currency", e.target.value)} data-testid="inv-currency" className="mt-1 w-full h-10 rounded-md bg-background border border-input px-3 text-sm">
                  {["INR", "USD", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Vendor GSTIN</Label>
                <Input value={fields.gstin || ""} onChange={(e) => setField("gstin", e.target.value)} data-testid="inv-gstin" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Vendor PAN</Label>
                <Input value={fields.pan || ""} onChange={(e) => setField("pan", e.target.value)} data-testid="inv-pan" className="mt-1" />
              </div>
              {(() => {
                const base = Number(fields.sub_total) > 0 ? Number(fields.sub_total) : Number(fields.amount || 0);
                const rate = Number(fields.tds_rate) || 0;
                const tds = Math.round(base * rate) / 100;
                const net = Math.max(0, Number(fields.amount || 0) - tds);
                return (
                  <div className="col-span-2 rounded-lg border border-amber-300/50 bg-amber-50/60 dark:bg-amber-500/10 p-3 space-y-2" data-testid="inv-tds-panel">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-amber-700 dark:text-amber-400">TDS to deduct (on taxable value, before GST)</Label>
                      {fields.matched_vendor?.tds_rate > 0 && (
                        <span className="text-[10px] text-muted-foreground">from vendor master</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Rate %</Label>
                        <Input type="number" step="0.01" value={fields.tds_rate || 0} onChange={(e) => setField("tds_rate", Number(e.target.value))} data-testid="inv-tds-rate" className="mt-0.5 h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Section</Label>
                        <Input value={fields.tds_section || ""} onChange={(e) => setField("tds_section", e.target.value)} placeholder="194J" data-testid="inv-tds-section" className="mt-0.5 h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">TDS amount</Label>
                        <p className="mt-0.5 h-8 flex items-center font-mono text-sm" data-testid="inv-tds-amount">{formatMoney(tds, fields.currency || "INR")}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Net payable to vendor</Label>
                        <p className="mt-0.5 h-8 flex items-center font-mono text-sm text-foreground" data-testid="inv-tds-net">{formatMoney(net, fields.currency || "INR")}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Input value={fields.description || ""} onChange={(e) => setField("description", e.target.value)} data-testid="inv-description" className="mt-1" />
              </div>
              <div className="col-span-2 flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2 mt-1">
                <span>Total will be posted to Vendors Payable as <b className="text-foreground">Payable</b>.</span>
                <span className="font-mono text-foreground">{formatMoney(fields.amount || 0, fields.currency || "INR")}</span>
              </div>
            </div>
          )}

          <DialogFooter>
            {stage === "pick" && (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                <Button onClick={previewFile} disabled={previewing || !file} data-testid="preview-invoice-btn" className="gap-2">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  {previewing ? "Reading…" : "Preview"}
                </Button>
              </>
            )}
            {stage === "preview" && (
              <>
                <Button variant="outline" onClick={() => setPreview(null)} disabled={parsing}>Back</Button>
                <Button onClick={parse} disabled={parsing} data-testid="confirm-invoice-parse-btn" className="gap-2">
                  {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {parsing ? "AI extracting…" : "Confirm & AI-Extract"}
                </Button>
              </>
            )}
            {stage === "review" && (
              <>
                <Button variant="outline" onClick={() => setFields(null)} disabled={posting}>Back</Button>
                <Button onClick={post} disabled={posting || !fields?.vendor || !fields?.amount} data-testid="save-invoice-payable-btn" className="gap-2">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
                  Save as Payable
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
